import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStateSelector, useCooperativeId } from 'hooks';
import format from 'date-fns/format';
import { FiscalYearsRes, FiscalYear } from 'models';
import { getCurrentCooperative } from 'store/home/selectors';
import services from 'services';

import {
  extractCurrentFiscalYear,
  toDate,
  filterBalanceFiscalYears,
  getBalanceEndDate,
} from 'utils';

import { CooperativeBudgetDetails } from 'components';

interface CooperativeBudgetState {
  version: {
    label: string;
    value: number;
  };
  fiscalYearsData: FiscalYearsRes;
  selectedFiscalYears: FiscalYear | null;
  startDate: Date | null;
  endDate: Date | null;
  loading: boolean;
  error: boolean;
}

type Version = {
  label: string;
  value: number;
};

function buildURL(
  coopId: string | null,
  fiscalYearId: string,
  startDate: Date,
  endDate: Date,
  version: Version
) {
  let q = '';

  if (fiscalYearId) {
    q += `fiscalYearId=${fiscalYearId}&`;
  }

  if (startDate) {
    q += `startDate=${format(startDate, "yyyy-MM-dd'T'00:00:00.000")}&`;
  }

  if (endDate) {
    q += `endDate=${format(endDate, "yyyy-MM-dd'T'00:00:00.000")}&`;
  }

  if (version.value !== null) {
    q += `shortLongVersion=${version.value}`;
  }

  return `/reports/cooperative/budget/${coopId}?${q.toString()}`;
}

const fetchFiscalYears = async (coopId: string) => {
  try {
    const res: FiscalYearsRes = (
      await services.get(`/api/cooperatives/${coopId}/fiscalyears`)
    ).data.Data;

    return res;
  } catch (err) {
    console.error(err);
  }
};

const CooperativeBudget = () => {
  const { t } = useTranslation();
  const coopId = useCooperativeId();
  const coopName = useStateSelector(
    (state) => getCurrentCooperative(state)?.Name || null
  );

  const [state, setState] = useState<CooperativeBudgetState>({
    version: {
      label: t('#portal.report.bar.option.label.VersionSelect.Short'),
      value: 0,
    },
    fiscalYearsData: {
      ClosedPeriodEnd: null,
      FiscalYears: [],
      NotMappedPaymentDate: null,
    },
    startDate: null,
    endDate: null,
    selectedFiscalYears: null,
    loading: false,
    error: false,
  });

  useEffect(() => {
    const fetchData = async (coopId: string) => {
      setState((prevState) => ({ ...prevState, loading: true }));

      try {
        const fiscalYearsData = await fetchFiscalYears(coopId);
        const fiscalYears = filterBalanceFiscalYears(
          fiscalYearsData,
          new Date()
        );
        const currentFiscalYear = extractCurrentFiscalYear(fiscalYears);
        const startDate = toDate(currentFiscalYear?.StartDate);
        const endDate = toDate(currentFiscalYear?.EndDate);

        setState((prevState) => ({
          ...prevState,
          fiscalYearsData: {
            ClosedPeriodEnd: fiscalYearsData?.ClosedPeriodEnd || null,
            FiscalYears: fiscalYears,
            NotMappedPaymentDate: fiscalYearsData?.NotMappedPaymentDate || null,
          },
          selectedFiscalYears: currentFiscalYear,
          startDate,
          endDate: endDate ? getBalanceEndDate(endDate) : null,
          loading: false,
          error: false,
        }));
      } catch (err) {
        console.error(err);
        setState((prevState) => ({
          ...prevState,
          loading: false,
          error: true,
        }));
      }
    };

    if (coopId) {
      fetchData(coopId);
    }
  }, [coopId]);

  const handleChangeFiscalYear = useCallback((fiscalYear: FiscalYear) => {
    const fiscalYearEndDate = toDate(fiscalYear.EndDate);
    const newEndDate = fiscalYearEndDate
      ? getBalanceEndDate(fiscalYearEndDate)
      : null;

    setState((prevState) => ({
      ...prevState,
      selectedFiscalYears: fiscalYear,
      startDate: toDate(fiscalYear.StartDate),
      endDate: newEndDate,
    }));
  }, []);

  const handleChangeStartDate = useCallback((date: Date | null) => {
    setState((prevState) => ({
      ...prevState,
      startDate: date,
    }));
  }, []);

  const handleChangeEndDate = useCallback((date: Date | null) => {
    setState((prevState) => ({
      ...prevState,
      endDate: date,
    }));
  }, []);

  const handleChangeVersion = useCallback((version: Version) => {
    setState((prevState) => ({
      ...prevState,
      version,
    }));
  }, []);

  const iframeURL = useMemo(() => {
    const { selectedFiscalYears, startDate, endDate, version } = state;
    if (selectedFiscalYears && startDate && endDate && version) {
      return buildURL(
        coopId,
        selectedFiscalYears.Id,
        startDate,
        endDate,
        version
      );
    }

    return '';
  }, [
    state.selectedFiscalYears,
    state.startDate,
    state.endDate,
    state.version,
    coopId,
  ]);

  const minDate = useMemo(() => {
    return toDate(state.selectedFiscalYears?.StartDate);
  }, [state.selectedFiscalYears?.StartDate]);

  const maxDate = useMemo(() => {
    const endDate = toDate(state.selectedFiscalYears?.EndDate);

    return endDate ? getBalanceEndDate(endDate) : null;
  }, [state.selectedFiscalYears]);

  const fileName = useMemo(() => {
    const startDate = state.startDate
      ? format(state.startDate, 'd.M.yyyy')
      : '';
    const endDate = state.endDate ? format(state.endDate, 'd.M.yyyy') : '';

    return `_${coopName}__Talousarviovertailu__${startDate} - ${endDate}`;
  }, [coopName, state.startDate, state.endDate]);

  return (
    <CooperativeBudgetDetails
      {...state}
      balanceType={null}
      onChangeBalanceType={() => {}}
      reportId="cooperative-budget"
      fileName={fileName}
      minDate={minDate}
      maxDate={maxDate}
      iframeURL={iframeURL}
      onChangeStartDate={handleChangeStartDate}
      onChangeEndDate={handleChangeEndDate}
      onChangeFiscalYear={handleChangeFiscalYear}
      onChangeVersion={handleChangeVersion}
    />
  );
};

export default CooperativeBudget;
